import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ThemeManager } from '@/lib/theme-manager';
import { useBranding } from '@/hooks/useBranding';
import { API_URL } from '@/lib/api-client';

type AuthPage = 'login' | 'signup' | 'forgot-password' | 'reset-password' | 'pending-approval';

interface ResetPasswordPageProps {
  onNavigate: (page: AuthPage) => void;
}

export function ResetPasswordPage({ onNavigate }: ResetPasswordPageProps) {
  const { branding } = useBranding();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    ThemeManager.getInstance().loadTheme();
    const params = new URLSearchParams(window.location.search);
    const resetToken = params.get('reset_token');
    setToken(resetToken);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!token) {
      setError('Missing reset token. Please request a new password reset.');
      return;
    }

    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Reset failed');
      }

      setSuccess(true);
      // Clean up the URL
      window.history.replaceState({}, '', window.location.pathname);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset failed');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-charcoal-950 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        {/* Branding Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-royal-gradient flex items-center justify-center shadow-glow-lg">
            <img
              src={branding.logoUrl}
              alt={branding.appName}
              className="w-10 h-10 rounded-xl object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
          <h1 className="text-3xl font-bold text-royal-gradient mb-2 font-heading">
            {branding.appName}
          </h1>
          <p className="text-slate-400">{branding.tagline}</p>
        </div>

        {/* Form Card */}
        <div className="glass rounded-2xl p-8 shadow-glass">
          {success ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center"
            >
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Password Reset</h3>
              <p className="text-sm text-slate-400 mb-6">
                Your password has been successfully reset. You can now sign in with your new password.
              </p>
              <button
                onClick={() => onNavigate('login')}
                className="px-6 py-2 rounded-lg bg-royal-gradient text-white font-medium
                         hover:opacity-90 transition-all duration-200 shadow-glow"
              >
                Sign In
              </button>
            </motion.div>
          ) : !token ? (
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Invalid Link</h3>
              <p className="text-sm text-slate-400 mb-6">
                This password reset link is invalid or has expired.
              </p>
              <button
                onClick={() => onNavigate('forgot-password')}
                className="text-sm text-amber-400 hover:text-amber-300 transition-colors"
              >
                Request a new reset link
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-white mb-2">Set New Password</h2>
              <p className="text-sm text-slate-400 mb-6">
                Enter your new password below.
              </p>

              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  <div className="flex items-center justify-between">
                    <span>{error}</span>
                    <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="reset-password" className="block text-sm text-slate-400 mb-2">
                    New Password
                  </label>
                  <input
                    id="reset-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full px-4 py-3 rounded-lg bg-charcoal-800 border border-charcoal-700
                             text-white placeholder-slate-500
                             focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent
                             transition-all duration-200"
                    placeholder="Min 8 characters"
                  />
                </div>

                <div>
                  <label htmlFor="reset-confirm" className="block text-sm text-slate-400 mb-2">
                    Confirm Password
                  </label>
                  <input
                    id="reset-confirm"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full px-4 py-3 rounded-lg bg-charcoal-800 border border-charcoal-700
                             text-white placeholder-slate-500
                             focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent
                             transition-all duration-200"
                    placeholder="Repeat password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 rounded-lg bg-royal-gradient text-white font-medium
                           hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2
                           focus:ring-offset-charcoal-900 disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-200 shadow-glow"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Resetting...
                    </span>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
